import SEO from "@/components/common/SEO";
import LegalLayout from "./LegalLayout";

import {
  organizationSchema,
  breadcrumbSchema,
} from "../../components/common/schema";

const schemas = [
  organizationSchema(),

  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Privacy Policy",
      path: "/privacy-policy",
    },
  ]),
];

export default function PrivacyPolicy() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Read Money Mart's Privacy Policy to understand how we collect, use, store and protect your personal information."
        canonical="/privacy-policy"
        structuredData={schemas}
      />
      <LegalLayout
        title="Privacy Policy"
        description="This Privacy Policy explains how Money Mart collects, uses, stores, and protects your personal information when you use our website or loan advisory services."
        lastUpdated="24 July 2026"
      >
        <h2>1. Introduction</h2>

        <p>
          Money Mart ("we", "our", or "us") values your privacy and is committed
          to protecting your personal information. This Privacy Policy explains
          how we collect, use, disclose, store, and safeguard your information
          when you visit our website, submit an enquiry, apply for loan
          assistance, communicate with us, or use any of our services.
        </p>

        <p>
          By accessing our website or providing your information, you consent to
          the practices described in this Privacy Policy.
        </p>

        <br />

        <h2>2. Who We Are</h2>

        <p>
          Money Mart operates as a loan advisory and Bank Channel Partner (DSA),
          helping customers connect with RBI-regulated Banks and NBFCs for
          financial products including home loans, mortgage loans, business
          loans, LAP, construction finance, personal loans and other lending
          products.
        </p>

        <p>We do not directly lend money or guarantee loan approvals.</p>

        <br />

        <h2>3. Information We Collect</h2>

        <p>Depending on the service requested, we may collect:</p>

        <ul>
          <li>Full Name</li>
          <li>Mobile Number</li>
          <li>Email Address</li>
          <li>Date of Birth</li>
          <li>Residential Address</li>
          <li>City and State</li>
          <li>PAN Card Details</li>
          <li>Aadhaar Details (where applicable)</li>
          <li>Employment Information</li>
          <li>Business Details</li>
          <li>Income Information</li>
          <li>Bank Details (when required)</li>
          <li>Existing Loan Information</li>
          <li>Property Details</li>
          <li>KYC Documents</li>
          <li>Financial Documents submitted during loan processing</li>
        </ul>

        <br />

        <h2>4. Information Collected Automatically</h2>

        <p>When you use our website, we may automatically collect:</p>

        <ul>
          <li>IP Address</li>
          <li>Browser Type</li>
          <li>Operating System</li>
          <li>Device Information</li>
          <li>Referral Source</li>
          <li>Visited Pages</li>
          <li>Time Spent on Website</li>
          <li>Cookies and Similar Technologies</li>
          <li>Advertising Campaign Information</li>
          <li>UTM Parameters</li>
        </ul>

        <br />

        <h2>5. How We Use Your Information</h2>

        <p>Your information may be used to:</p>

        <ul>
          <li>Assess loan eligibility.</li>
          <li>Connect you with suitable Banks and NBFCs.</li>
          <li>Verify your identity.</li>
          <li>Process loan enquiries.</li>
          <li>Contact you regarding your application.</li>
          <li>Respond to your queries.</li>
          <li>Improve our services.</li>
          <li>Prevent fraud and misuse.</li>
          <li>Comply with legal and regulatory obligations.</li>
          <li>Improve customer experience.</li>
          <li>Maintain internal records.</li>
        </ul>

        <br />

        <h2>6. Marketing Communications</h2>

        <p>
          By submitting your contact information, you authorize Money Mart to
          contact you through phone calls, SMS, WhatsApp, email, or other
          digital communication channels regarding loan products, eligibility,
          application status, promotional offers, and related financial
          services.
        </p>

        <p>
          You may opt out of promotional communications at any time by
          contacting us.
        </p>

        <br />

        <h2>7. Sharing of Information</h2>

        <p>Your information may be shared with:</p>

        <ul>
          <li>RBI-regulated Banks.</li>
          <li>Registered NBFCs.</li>
          <li>Credit Assessment Agencies.</li>
          <li>KYC Verification Partners.</li>
          <li>Document Verification Partners.</li>
          <li>Technology Service Providers.</li>
          <li>CRM and Cloud Service Providers.</li>
          <li>Legal or Regulatory Authorities where required by law.</li>
        </ul>

        <p>We do not sell your personal information to third parties.</p>

        <br />

        <h2>8. Data Security</h2>

        <p>
          We implement reasonable technical and organizational safeguards to
          protect your information from unauthorized access, misuse, disclosure,
          alteration, or destruction.
        </p>

        <p>
          While we strive to use commercially acceptable means to protect your
          data, no method of transmission over the Internet or electronic
          storage is completely secure.
        </p>

        <br />

        <h2>9. Data Retention</h2>

        <p>
          We retain your personal information only for as long as necessary to
          fulfil the purposes described in this Privacy Policy, comply with
          legal obligations, resolve disputes, and enforce our agreements.
        </p>

        <br />

        <h2>10. Cookies</h2>

        <p>
          Our website uses cookies and similar technologies to improve website
          functionality, remember preferences, analyze traffic, and measure the
          effectiveness of advertising campaigns.
        </p>

        <br />

        <p>
          You may disable cookies through your browser settings, although
          certain website features may not function properly.
        </p>

        <br />

        <h2>11. Analytics</h2>

        <p>
          We may use analytics and advertising tools including Google Analytics,
          Meta Pixel and similar technologies to understand website usage,
          optimize campaigns, and improve user experience.
        </p>

        <br />

        <h2>12. Third-Party Websites</h2>

        <p>
          Our website may contain links to third-party websites. We are not
          responsible for their privacy practices or content. Users are
          encouraged to review the privacy policies of such websites
          independently.
        </p>

        <br />

        <h2>13. Children's Privacy</h2>

        <p>
          Our services are intended only for individuals who are legally
          eligible to enter into financial agreements. We do not knowingly
          collect information from minors.
        </p>

        <br />

        <h2>14. Your Rights</h2>

        <p>Subject to applicable laws, you may request to:</p>

        <ul>
          <li>Access your personal information.</li>
          <li>Correct inaccurate information.</li>
          <li>Update your information.</li>
          <li>Withdraw consent where applicable.</li>
          <li>
            Request deletion of your personal information, subject to legal
            obligations.
          </li>
        </ul>

        <br />

        <h2>15. Changes to this Privacy Policy</h2>

        <p>
          We may update this Privacy Policy periodically to reflect changes in
          our practices, legal requirements, or business operations. The updated
          version will always be available on this page along with the latest
          revision date.
        </p>

        <br />

        <h2>16. Contact Us</h2>

        <p>
          If you have any questions regarding this Privacy Policy or wish to
          exercise your privacy rights, please contact Money Mart using the
          contact details provided on our website.
        </p>
      </LegalLayout>
    </>
  );
}
